import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HeartHandshake, Mic, Clock, ArrowRight, Sparkles, Ear, MessagesSquare,
  TrendingUp, Hourglass, XCircle, CheckCircle2, Crown,
} from 'lucide-react';
import LegalFooter from '../components/LegalFooter';
import AperitivoSection from '../components/AperitivoSection';
import { useLandingMetadata } from '../hooks/useLandingMetadata';

/**
 * Nymia · Landing "Psicologi & Laureati in Psicologia" — /join-community
 * Campaign-only page (Meta/Google/LinkedIn/Indeed Ads): NOT linked in any menu.
 * Dark & Gold, cinematic, mobile-first. Copy fornito dal founder (IT only).
 */

const GOLD = '#D4AF37';
const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/02b481b2d278beeff753664d1fd78a9c58a6451b66999af551ad6483c9b611c4.jpeg',
  listening: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/dec71e1b18bc0a34dba1a69d83531aeadad5d6608e962c0388772a332353a948.jpeg',
  voice: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/248f8d66c3a5501f085f1d1b10aa6a6ceaf11c28183bef7d191a10bdad5f2e06.jpeg',
};

const easeCine = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: easeCine },
  }),
};

const Serif = ({ children, className = '' }) => (
  <span className={className} style={{ fontFamily: "'Playfair Display', serif" }}>{children}</span>
);

const CtaButton = ({ testId, className = '' }) => (
  <Link
    to="/register"
    className={`inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-bold text-black shadow-[0_8px_40px_rgba(212,175,55,0.35)] transition-transform hover:scale-[1.03] sm:px-10 sm:text-lg ${className}`}
    style={{ backgroundColor: GOLD }}
    data-testid={testId}
  >
    Diventa una Voce Pro Nymia
    <ArrowRight className="h-5 w-5" />
  </Link>
);

const Header = () => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl" data-testid="join-header">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
      <Link to="/" className="flex items-center gap-2.5" data-testid="join-logo">
        <Serif className="text-2xl font-semibold leading-none" >
          <span style={{ color: GOLD }}>N</span>
        </Serif>
        <span className="text-sm font-semibold uppercase tracking-[0.35em] text-white">Nymia</span>
      </Link>
      <div className="flex items-center gap-2.5">
        <Link
          to="/login"
          className="rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:border-white/40 sm:px-5"
          data-testid="join-login-btn"
        >
          Accedi
        </Link>
        <Link
          to="/register"
          className="rounded-full px-4 py-2 text-[13px] font-bold text-black transition-opacity hover:opacity-90 sm:px-5"
          style={{ backgroundColor: GOLD }}
          data-testid="join-register-btn"
        >
          Registrati
        </Link>
      </div>
    </div>
  </header>
);

export default function JoinCommunityPage() {
  useLandingMetadata('join');
  return (
    <div className="min-h-screen bg-[#050505] text-white" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="join-community-page">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden pt-16" data-testid="join-hero">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="h-full w-full object-cover object-center opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/40 to-[#050505]" />
        </div>
        <div className="relative mx-auto w-full max-w-4xl px-5 py-20 text-center">
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.4em]" style={{ color: GOLD }}>
            Per chi ha studiato Psicologia
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="text-4xl leading-[1.12] sm:text-5xl lg:text-6xl" data-testid="join-hero-title">
            <Serif>La tua capacità di ascoltare<br />ha un <span style={{ color: GOLD }}>valore</span>.</Serif>
          </motion.h1>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mx-auto mt-7 max-w-2xl space-y-3 text-base leading-relaxed text-white/75 md:text-lg">
            <p className="font-semibold text-white/90">Hai studiato Psicologia?<br className="sm:hidden" /> Credi che essere ascoltati possa fare la differenza?</p>
            <p>Su Nymia puoi mettere in pratica le tue competenze comunicative offrendo semplicemente presenza, ascolto e conversazioni autentiche.</p>
            <p className="text-white/60" data-testid="join-voice-only-copy">Bastano la tua voce e la voglia di ascoltare.<br /><Serif className="text-lg italic text-white/85">Solo persone.</Serif></p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10">
            <CtaButton testId="join-hero-cta" />
          </motion.div>
        </div>
      </section>

      {/* ── PERCHÉ NYMIA È DIVERSA ───────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:py-28" data-testid="join-diversa">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mb-12 text-center text-3xl sm:text-4xl">
          <Serif>Perché Nymia è <span style={{ color: GOLD }}>diversa</span></Serif>
        </motion.h2>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { icon: HeartHandshake, title: 'Ascolto autentico', text: "Metti in pratica l'ascolto attivo attraverso conversazioni reali con persone che desiderano semplicemente essere ascoltate." },
            { icon: Mic, title: 'Solo voce', text: 'Ascolto, rispetto e conversazioni autentiche.', extra: 'La voce mette al centro la persona.' },
            { icon: Clock, title: 'Libertà totale', text: 'Decidi tu quando collegarti. Decidi tu quanto tempo dedicare. Gestisci tutto direttamente dal tuo smartphone.' },
          ].map((c, i) => (
            <motion.div key={c.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors duration-300 hover:border-[#D4AF37]/40"
              data-testid={`join-card-${i}`}>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: 'rgba(212,175,55,0.12)' }}>
                <c.icon className="h-6 w-6" style={{ color: GOLD }} strokeWidth={1.6} />
              </div>
              <h3 className="mb-3 text-xl font-semibold"><Serif>{c.title}</Serif></h3>
              <p className="text-sm leading-relaxed text-white/70">{c.text}</p>
              {c.extra && <p className="mt-3 text-sm italic leading-relaxed" style={{ color: GOLD }}>{c.extra}</p>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── PERCHÉ MOLTI INIZIANO DA QUI ─────────────────────── */}
      <section className="border-y border-white/5 bg-white/[0.02] py-20 md:py-28" data-testid="join-iniziano">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative">
            <img src={IMG.listening} alt="Un momento di calma e ascolto" className="w-full rounded-2xl object-cover shadow-2xl shadow-black/60" loading="lazy" />
            <div className="absolute -bottom-4 -right-4 hidden rounded-2xl border border-[#D4AF37]/30 bg-[#050505] px-5 py-3 md:block">
              <p className="text-xs uppercase tracking-widest text-white/50">Il tuo tempo</p>
              <p className="text-lg font-semibold" style={{ color: GOLD }}><Serif>ha valore</Serif></p>
            </div>
          </motion.div>
          <div>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mb-8 text-3xl sm:text-4xl">
              <Serif>Perché molti <span style={{ color: GOLD }}>iniziano da qui</span></Serif>
            </motion.h2>
            <div className="space-y-4">
              {[
                { icon: Ear, text: 'Metti in pratica le tue competenze comunicative.' },
                { icon: MessagesSquare, text: "Migliori la capacità di ascolto." },
                { icon: HeartHandshake, text: "Accresci la tua esperienza nelle conversazioni umane." },
                { icon: TrendingUp, text: 'Costruisci gradualmente la tua reputazione.' },
                { icon: Hourglass, text: 'Valorizzi il tuo tempo.' },
              ].map((r, i) => (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="flex items-center gap-4 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(212,175,55,0.12)' }}>
                    <r.icon className="h-4.5 w-4.5" style={{ color: GOLD, width: 18, height: 18 }} strokeWidth={1.8} />
                  </span>
                  <p className="text-[15px] text-white/85">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── NESSUN ALGORITMO ─────────────────────────────────── */}
      <section className="py-24 md:py-36" data-testid="join-algoritmo">
        <div className="mx-auto max-w-3xl px-5 text-center">
          {['Nessun algoritmo.', 'Nessun follower.', 'Nessun contenuto da pubblicare.'].map((line, i) => (
            <motion.p key={line} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="text-3xl leading-snug text-white/85 sm:text-4xl lg:text-5xl">
              <Serif>{line}</Serif>
            </motion.p>
          ))}
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={4}
            className="mt-8 text-4xl sm:text-5xl lg:text-6xl">
            <Serif><span style={{ color: GOLD }}>Solo persone.</span></Serif>
          </motion.p>
        </div>
      </section>

      {/* ── APERITIVO NYMIA (gestito da Admin) ───────────────── */}
      <AperitivoSection source="join-community" />

      {/* ── SEZIONE EMOZIONALE ───────────────────────────────── */}
      <section className="relative overflow-hidden py-20 md:py-28" data-testid="join-emozionale">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2">
          <div className="order-2 md:order-1">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mb-7 text-3xl leading-tight sm:text-4xl">
              <Serif>Ogni giorno qualcuno cerca semplicemente <span style={{ color: GOLD }}>una voce</span>.</Serif>
            </motion.h2>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="space-y-4 text-[15px] leading-relaxed text-white/70 md:text-base">
              <p>Molte persone non cercano una diagnosi.<br />Non cercano una terapia.<br />Non cercano qualcuno che dica loro cosa fare.</p>
              <p className="text-white/90">Cercano semplicemente qualcuno disposto ad ascoltarle.</p>
              <p>A volte bastano pochi minuti di ascolto sincero per far sentire una persona meno sola.</p>
              <p className="italic" style={{ color: GOLD }}>
                Se l'ascolto è una delle tue qualità, su Nymia puoi trasformarlo in qualcosa di utile per gli altri e prezioso anche per te.
              </p>
            </motion.div>
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="order-1 md:order-2">
            <img src={IMG.voice} alt="Una voce che ascolta" className="w-full rounded-2xl object-cover shadow-2xl shadow-black/60" loading="lazy" />
          </motion.div>
        </div>
      </section>

      {/* ── PERCHÉ ENTRARE ADESSO ────────────────────────────── */}
      <section className="relative overflow-hidden border-y border-[#D4AF37]/20 py-20 md:py-28" data-testid="join-adesso"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10) 0%, rgba(5,5,5,0) 60%)' }}>
        <div className="mx-auto max-w-3xl px-5 text-center">
          <motion.span variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em]"
            style={{ color: GOLD }} data-testid="join-early-badge">
            <Crown className="h-3.5 w-3.5" /> Early Voce Pro
          </motion.span>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="mb-7 text-3xl leading-tight sm:text-4xl lg:text-5xl">
            <Serif>Le prime Voci Pro costruiscono <span style={{ color: GOLD }}>il proprio spazio</span>.</Serif>
          </motion.h2>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
            className="space-y-4 text-[15px] leading-relaxed text-white/70 md:text-base">
            <p>Ogni piattaforma attraversa una fase iniziale in cui è possibile costruire la propria presenza con meno concorrenza.</p>
            <p>Chi arriva oggi ha più tempo per creare fiducia, raccogliere recensioni, fidelizzare i primi utenti e diventare un punto di riferimento nella propria categoria.</p>
            <p className="font-semibold text-white/90">Essere tra i primi significa avere più opportunità di costruire la propria reputazione nel tempo.</p>
          </motion.div>
        </div>
      </section>

      {/* ── UNO SPAZIO DIVERSO ───────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-5 py-20 md:py-28" data-testid="join-spazio-diverso">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mb-10 text-center text-3xl sm:text-4xl">
          <Serif>Uno spazio <span style={{ color: GOLD }}>diverso</span></Serif>
        </motion.h2>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9">
          <div className="space-y-3.5">
            {[
              'Su Nymia NON si svolgono sedute psicologiche.',
              'NON si effettuano diagnosi.',
              'NON si offrono percorsi terapeutici.',
            ].map((line) => (
              <p key={line} className="flex items-start gap-3 text-[15px] font-semibold text-white/85 md:text-base">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400/80" strokeWidth={1.8} />
                {line}
              </p>
            ))}
            <div className="my-5 h-px bg-white/10" />
            <p className="flex items-start gap-3 text-[15px] leading-relaxed text-white/75 md:text-base">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
              Le conversazioni presenti sulla piattaforma sono semplici momenti di ascolto umano tra persone.
            </p>
            <p className="flex items-start gap-3 text-[15px] leading-relaxed text-white/75 md:text-base">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
              Nymia vuole valorizzare il tempo, l'empatia e la capacità di ascoltare.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── CTA FINALE ───────────────────────────────────────── */}
      <section className="relative overflow-hidden py-24 md:py-32" data-testid="join-final-cta">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.12) 0%, rgba(5,5,5,0) 65%)' }} />
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-3xl leading-tight sm:text-4xl lg:text-5xl">
            <Serif>Le persone hanno bisogno di essere <span style={{ color: GOLD }}>ascoltate</span>.</Serif>
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
            Se credi nel valore dell'ascolto, questo è il momento giusto per iniziare.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="mt-10">
            <CtaButton testId="join-final-cta-btn" />
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="border-t border-white/5 px-5 pb-10 pt-12" data-testid="join-footer">
        <div className="mx-auto max-w-3xl">
          <p className="rounded-xl border border-white/10 bg-white/[0.02] p-5 text-center text-xs leading-relaxed text-white/50" data-testid="join-disclaimer">
            Nymia è una piattaforma dedicata alle conversazioni tra persone e non sostituisce psicologi,
            psicoterapeuti o altri professionisti sanitari. Le attività svolte sulla piattaforma non
            costituiscono prestazioni sanitarie o terapeutiche. In presenza di disagio psicologico o
            situazioni di emergenza è sempre necessario rivolgersi ai servizi competenti.
          </p>
        </div>
        <LegalFooter className="mx-auto mt-8 max-w-3xl" />
      </footer>
    </div>
  );
}
